import { useState, useRef } from "react";
import { Upload, FileText, Trash2, X } from "lucide-react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { toast } from "sonner";
import { deeparEffectsAPI } from "../utils/deeparEffectsAPI";

export const EffectUploader = ({
  onEffectsUpdate,
  uploadedEffects,
  eventId,
  onRefreshList,
}) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [effectName, setEffectName] = useState("");

  // Dialog states for confirmations
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    effect: null,
  });
  const [clearAllDialog, setClearAllDialog] = useState(false);

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!eventId) {
      toast.error("Event ID is required to upload effects");
      return;
    }

    // For single file, show name dialog
    if (files.length === 1) {
      const file = files[0];
      if (!file.name.endsWith(".deepar")) {
        toast.error("Only .deepar files are supported");
        return;
      }

      setSelectedFile(file);
      setEffectName(file.name.replace(".deepar", ""));
      setShowNameDialog(true);
    } else {
      // For multiple files, upload directly with filename
      handleMultipleFileUpload(files);
    }
  };

  const handleSingleFileUpload = async () => {
    if (!selectedFile || !effectName.trim()) {
      toast.error("Please enter a name for the effect");
      return;
    }

    setIsUploading(true);
    setShowNameDialog(false);

    try {
      console.log(
        `[EffectUploader] Uploading ${selectedFile.name} with custom name: ${effectName}`
      );

      const response = await deeparEffectsAPI.upload(
        eventId,
        selectedFile,
        "filters",
        effectName.trim()
      );

      if (response.status === 201 || response.status === 200) {
        console.log(
          `[EffectUploader] Upload successful, triggering refresh...`
        );

        // Trigger list refresh in parent component to fetch latest from server
        if (onRefreshList) {
          await onRefreshList();
          console.log(`[EffectUploader] Main effects list refreshed`);
        }

        // Also update the uploader's local list for immediate feedback
        const newEffect = {
          _id: response.effect._id,
          name: response.effect.name,
          fileName: response.effect.fileName,
          category: response.effect.category,
          fileSize: response.effect.fileSize,
          effectFileUrl: response.effect.effectFileUrl,
          isUploaded: true,
          eventId: response.effect.eventId,
          createdAt: response.effect.createdAt,
        };

        const updatedEffects = [...uploadedEffects, newEffect];
        onEffectsUpdate(updatedEffects);
        console.log(`[EffectUploader] Local uploader list updated`);

        toast.success(`Effect "${effectName}" uploaded successfully`);
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      console.error(
        `[EffectUploader] Error uploading ${selectedFile.name}:`,
        error
      );
      toast.error(`Error uploading effect: ${error.message}`);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setEffectName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleMultipleFileUpload = async (files) => {
    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;
    const newEffects = [];

    try {
      for (const file of Array.from(files)) {
        if (!file.name.endsWith(".deepar")) {
          console.warn(
            `[EffectUploader] Skipping ${file.name}: Only .deepar files are supported`
          );
          toast.error(`Skipped ${file.name}: Only .deepar files supported`);
          failCount++;
          continue;
        }

        try {
          console.log(`[EffectUploader] Uploading ${file.name} to database...`);

          // Use filename without extension as default name
          const defaultName = file.name.replace(".deepar", "");
          const response = await deeparEffectsAPI.upload(
            eventId,
            file,
            "filters",
            defaultName
          );

          if (response.status === 201) {
            const newEffect = {
              _id: response.effect._id,
              name: response.effect.name,
              fileName: response.effect.fileName,
              category: response.effect.category,
              fileSize: response.effect.fileSize,
              effectFileUrl: response.effect.effectFileUrl,
              isUploaded: true,
              eventId: response.effect.eventId,
              createdAt: response.effect.createdAt,
            };
            newEffects.push(newEffect);
            successCount++;
          } else {
            throw new Error(response.message || "Upload failed");
          }
        } catch (fileError) {
          console.error(
            `[EffectUploader] Error uploading ${file.name}:`,
            fileError
          );
          toast.error(`Error uploading ${file.name}: ${fileError.message}`);
          failCount++;
        }
      }

      // Update both local uploader list and main effects list
      if (successCount > 0) {
        console.log(
          `[EffectUploader] ${successCount} files uploaded, updating lists...`
        );

        // Update local uploader list
        const updatedEffects = [...uploadedEffects, ...newEffects];
        onEffectsUpdate(updatedEffects);
        console.log(
          `[EffectUploader] Local uploader list updated with ${newEffects.length} effects`
        );

        // Refresh main effects list from server
        if (onRefreshList) {
          await onRefreshList();
          console.log(`[EffectUploader] Main effects list refreshed`);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} effect(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to upload ${failCount} effect(s)`);
      }
    } catch (error) {
      console.error("[EffectUploader] Critical error:", error);
      toast.error("Critical error during upload");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDialogClose = () => {
    setShowNameDialog(false);
    setSelectedFile(null);
    setEffectName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeEffect = (effectToRemove) => {
    setDeleteDialog({ open: true, effect: effectToRemove });
  };

  const confirmRemoveEffect = async () => {
    if (!deleteDialog.effect) return;

    try {
      if (deleteDialog.effect._id) {
        // Delete from database
        await deeparEffectsAPI.deleteEffect(deleteDialog.effect._id);
        toast.success("Effect removed from database");

        // Trigger list refresh in parent component
        if (onRefreshList) {
          await onRefreshList();
        }
      }
    } catch (error) {
      console.error("Failed to delete effect:", error);
      toast.error(`Failed to delete effect: ${error.message}`);
    } finally {
      setDeleteDialog({ open: false, effect: null });
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, effect: null });
  };

  const clearAllEffects = () => {
    setClearAllDialog(true);
  };

  const confirmClearAllEffects = async () => {
    try {
      if (eventId) {
        // Clear all effects from database for this event
        await deeparEffectsAPI.clearEffects(eventId);
        toast.success("All effects cleared from database");

        // Trigger list refresh in parent component
        if (onRefreshList) {
          await onRefreshList();
        }
      }
    } catch (error) {
      console.error("Failed to clear effects:", error);
      toast.error(`Failed to clear effects: ${error.message}`);
    } finally {
      setClearAllDialog(false);
    }
  };

  const closeClearAllDialog = () => {
    setClearAllDialog(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Upload Section */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Upload className="h-5 w-5" />
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Upload Effects
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".deepar"
            multiple
            onChange={handleFileSelect}
            disabled={isUploading}
            style={{
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />

          <Typography variant="caption" color="text.secondary">
            Upload .deepar effect files. For single files, you can customize the
            display name.
          </Typography>

          {isUploading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  border: "2px solid",
                  borderColor: "primary.main",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography variant="caption" color="primary">
                Processing files...
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Name Input Dialog */}
      <Dialog
        open={showNameDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Customize Effect Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Effect Display Name"
            fullWidth
            variant="outlined"
            value={effectName}
            onChange={(e) => setEffectName(e.target.value)}
            placeholder="Enter a custom name for this effect"
            sx={{ mt: 2 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            This name will appear in the effects list. Default:{" "}
            {selectedFile?.name?.replace(".deepar", "")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSingleFileUpload}
            variant="contained"
            disabled={!effectName.trim() || isUploading}
            sx={{
              color: "white",
            }}
          >
            {isUploading ? "Uploading..." : "Upload Effect"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Effect Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Trash2 className="h-5 w-5" />
            Delete Effect
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to delete the effect{" "}
            <strong>"{deleteDialog.effect?.name}"</strong>?
          </Typography>
          <Typography>
            This action cannot be undone. The effect file will be permanently
            removed from the server.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            onClick={confirmRemoveEffect}
            variant="contained"
            sx={{ color: "white" }}
          >
            Delete Effect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Effects Confirmation Dialog */}
      <Dialog
        open={clearAllDialog}
        onClose={closeClearAllDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Trash2 className="h-5 w-5" />
            Clear All Effects
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to delete all {uploadedEffects.length}{" "}
            uploaded effects for this event?
          </Typography>
          <Typography>
            This action cannot be undone. All effect files will be permanently
            removed from the server.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearAllDialog}>Cancel</Button>
          <Button
            onClick={confirmClearAllEffects}
            variant="contained"
            sx={{
              color: "white",
            }}
            startIcon={<Trash2 className="h-4 w-4" />}
          >
            Clear All Effects
          </Button>
        </DialogActions>
      </Dialog>

      {/* Uploaded Effects List */}
      {uploadedEffects.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileText className="h-5 w-5" />
              <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
                Uploaded Effects
              </Typography>
              <Chip
                label={uploadedEffects.length}
                size="small"
                sx={{ height: "20px", fontSize: "0.75rem" }}
              />
            </Box>
            <IconButton onClick={clearAllEffects}>
              <Trash2 className="h-4 w-4" />
            </IconButton>
          </Box>

          <Box sx={{ maxHeight: "128px", overflow: "auto" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {uploadedEffects.map((effect, index) => (
                <Box
                  key={`${effect._id || effect.path}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    backgroundColor: "grey.100",
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "medium", display: "block" }}
                      noWrap
                    >
                      {effect.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {effect.category}
                      {effect.fileSize &&
                        ` • ${(effect.fileSize / 1024).toFixed(1)}KB`}
                    </Typography>
                  </Box>
                  <IconButton onClick={() => removeEffect(effect)}>
                    <X className="h-4 w-4" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
